import { tokenStorage } from '@/features/auth/auth-session'
import { Dot } from '@/features/map/types/shape'
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { useEffect, useMemo, useState } from 'react'
import { TagData, TagObservation } from '../types/trackLocation'

type SignalRPayload = {
  messageType: string
  floorId: string
  timestamp: string
  devices: TagData[]
}

type DevicePositionResult = {
  point: [number, number]
  gateway: Dot
  strongest: TagObservation
}

type Props = {
  floorId?: string
  gatewayDots: Dot[]
}

function rssiToDistance(rssi: number, txPower: number, n = 2.5) {
  return Math.pow(10, (txPower - rssi) / (10 * n))
}

function offsetPoint(point: [number, number], offset = 0.01): [number, number] {
  return [point[0] + offset, point[1] - offset]
}

function randomPointAround(point: [number, number], radius = 0.03): [number, number] {
  const angle = Math.random() * Math.PI * 2
  const distance = Math.random() * radius
  return [point[0] + Math.cos(angle) * distance, point[1] + Math.sin(angle) * distance]
}

function calculateDevicePosition(observations: TagObservation[], gateways: Dot[]): DevicePositionResult | null {
  if (observations.length === 0) return null

  let totalWeight = 0
  let weightedX = 0
  let weightedY = 0

  let strongest: TagObservation | null = null
  let strongestGateway: Dot | null = null

  for (const obs of observations) {
    // ignore weak signal
    if (obs.rssi < -85) continue

    const gateway = gateways.find(g => g.referenceId === obs.gatewayId)
    if (!gateway) continue

    // convert RSSI -> distance
    const distance = rssiToDistance(obs.rssi, obs.txPower)

    // stronger weighting
    const weight = 1 / Math.max(distance * distance, 0.0001)
    weightedX += gateway.point[0] * weight
    weightedY += gateway.point[1] * weight
    totalWeight += weight

    if (!strongest || obs.rssi > strongest.rssi) {
      strongest = obs
      strongestGateway = gateway
    }
  }

  if (totalWeight === 0 || !strongest || !strongestGateway) {
    return null
  }

  // only one gateway sees device
  if (observations.length === 1) {
    return {
      point: randomPointAround(strongestGateway.point),
      gateway: strongestGateway,
      strongest,
    }
  }

  // extremely close to gateway
  if (strongest.rssi > -40) {
    return {
      point: offsetPoint(strongestGateway.point),
      gateway: strongestGateway,
      strongest,
    }
  }

  return {
    point: [weightedX / totalWeight, weightedY / totalWeight],
    gateway: strongestGateway,
    strongest,
  }
}

export function useTrackingSignalR({ floorId, gatewayDots }: Props) {
  const [tags, setTags] = useState<TagData[]>([])
  const [connection, setConnection] = useState<HubConnection | null>(null)

  useEffect(() => {
    if (!floorId) return

    const hubConnection = new HubConnectionBuilder()
      .withUrl('https://ble-gateway-signalr-test.goodviewmedical.com/hub', {
        accessTokenFactory: () => {
          return tokenStorage.getToken() ?? ''
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    setConnection(hubConnection)
  }, [floorId])

  useEffect(() => {
    if (!connection) return

    async function start() {
      try {
        connection?.on('ReceiveSnapshot', (payload: SignalRPayload) => {
          // console.log('snapshot', payload)
          setTags(payload.devices ?? [])
        })

        connection?.on('ReceiveTag', (payload: SignalRPayload) => {
          // console.log('tag update', payload)
          setTags(payload.devices ?? [])
        })

        await connection?.start()
        // console.log('signalr connected')

        await connection?.invoke('JoinFloor', floorId)
      } catch (error) {
        console.error('signalr connection error', error)
      }
    }

    start()

    return () => {
      connection.off('ReceiveSnapshot')
      connection.off('ReceiveTag')

      connection.stop()
    }
  }, [connection, floorId])

  const deviceDots: Dot[] = useMemo(() => {
    return (tags ?? [])
      .map(tag => {
        const result = calculateDevicePosition(
          tag.observations,
          gatewayDots.filter(g => g.gatewayType === 'BLE'),
        )

        if (!result) return null

        return {
          id: tag.tagId,
          label: tag.tagId,
          dotType: 'device',
          color: '#22c55e',
          point: result.point,
          departmentId: result.gateway.departmentId,
          rssi: result.strongest.rssi,
          battery: tag.battery,
          gatewayId: result.gateway.id,
        } satisfies Dot
      })
      .filter(Boolean) as Dot[]
  }, [tags, gatewayDots])

  return {
    connection,
    tags,
    deviceDots,
  }
}
